import React, { useState } from 'react'
import { useDropzone } from 'react-dropzone'
import {
  ActionIcon,
  Anchor,
  Button,
  Group,
  Loader,
  Modal,
  ScrollArea,
  Stack,
  Text,
  Tooltip,
} from '@mantine/core'
import { useLocalStorage } from '@mantine/hooks'
import { showNotification } from '@mantine/notifications'
import {
  EnterFullScreenIcon,
  PlusIcon,
  UploadIcon,
} from '@radix-ui/react-icons'
import format from 'date-fns/format'
import parseISO from 'date-fns/parseISO'

import { AddExhibitsModal } from './AddExhibitsModal'
import {
  ExhibitFilePreviewModal,
  exhibitPreviewFile,
  PreviewFile,
} from './ExhibitFilePreview'
import { ExhibitFileLink, ReadOnlyExhibitIcon } from './ExhibitParts'
import {
  DESIGNATION_LABELS,
  EXHIBIT_FILE_ACCEPT,
  ExhibitSubmissionState,
  exhibitSubmissionState,
  hasCourtReporterExhibits,
  shownExhibits,
  submitBlocker,
} from './exhibits'
import { ExhibitsTableModal } from './ExhibitsTableModal'

import { CourtReporterExhibitSubmission } from '@/apiClient'
import { Card } from '@/components/Event/Detail/components/Card'
import { useExhibitsAccess } from '@/components/Event/hooks/useExhibitsAccess'
import { COURT_REPORTER_EXHIBITS_EMAIL } from '@/constants'
import { useModals } from '@/hooks/useModals'
import {
  ExhibitSubmissionEvent,
  useExhibitSubmissionQuery,
  useSubmitExhibitsMutation,
} from '@/queries/courtReporterExhibits'
import { getApiErrorMessage } from '@/utils/apiError'

export const ExhibitsSection: React.FC<{
  event: ExhibitSubmissionEvent
  title?: string
  // Only once the event is over can the CR know it had no exhibits.
  allowNoExhibits?: boolean
}> = ({ event, title, allowNoExhibits = false }) => {
  const {
    data: submission,
    isLoading,
    isError,
  } = useExhibitSubmissionQuery(event)

  return (
    <Stack spacing="xs">
      {title && (
        <Text size="sm" weight={600}>
          {title}
        </Text>
      )}

      {isLoading ? (
        <Loader size="sm" />
      ) : isError || !submission ? (
        <Text size="sm" color="red">
          Could not load the exhibits.
        </Text>
      ) : (
        <ExhibitSubmission
          event={event}
          submission={submission}
          allowNoExhibits={allowNoExhibits}
        />
      )}
    </Stack>
  )
}

export const ExhibitsCard: React.FC<{ event: ExhibitSubmissionEvent }> = ({
  event,
}) => {
  const hasAccess = useExhibitsAccess()
  if (!hasAccess) {
    return null
  }

  return (
    <Card heading="Exhibits">
      <ExhibitsSection event={event} />
    </Card>
  )
}

const ExhibitSubmission: React.FC<{
  event: ExhibitSubmissionEvent
  submission: CourtReporterExhibitSubmission
  allowNoExhibits: boolean
}> = ({ event, submission, allowNoExhibits }) => {
  const [adding, setAdding] = useState(false)
  const [expanded, setExpanded] = useState(false)
  const [previewing, setPreviewing] = useState<PreviewFile | null>(null)
  const [droppedFiles, setDroppedFiles] = useState<File[]>([])
  const state = exhibitSubmissionState(submission)
  const isOpen = state === 'open'
  const exhibits = shownExhibits(submission, state)

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    disabled: !isOpen || adding,
    onDrop: files => {
      if (files.length > 0) {
        setDroppedFiles(files)
        setAdding(true)
      }
    },
  })

  const addButton = (
    <Button
      size="xs"
      variant="default"
      leftIcon={<PlusIcon />}
      onClick={() => setAdding(true)}
    >
      Add exhibits
    </Button>
  )
  const submitButton = (
    <SubmitExhibitsButton
      event={event}
      submission={submission}
      allowNoExhibits={allowNoExhibits}
    />
  )

  return (
    <>
      <div {...getRootProps()} className="relative space-y-2">
        <input {...getInputProps()} accept={EXHIBIT_FILE_ACCEPT} />
        {isDragActive && exhibits.length > 0 && (
          <div className="pointer-events-none absolute -inset-2 z-10 flex flex-col items-center justify-center gap-1 rounded border-2 border-dashed border-indigo-500 bg-white/90">
            <UploadIcon width={24} height={24} />
            <Text size="sm" weight={600}>
              Drop files to add them as exhibits
            </Text>
          </div>
        )}

        <SubmissionNotice submission={submission} state={state} />
        {state === 'missed' && (
          <NoExhibitsDialog
            storageKey={`no-exhibits-dialog-seen:${event.exhibit_submission_url}`}
          />
        )}

        {exhibits.length === 0 ? (
          isOpen && (
            <div
              role="button"
              tabIndex={0}
              onClick={open}
              onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && open()}
              className={`cursor-pointer rounded border border-dashed p-4 text-center ${
                isDragActive
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-gray-300'
              }`}
            >
              <Text size="sm" color="dimmed">
                Drop exhibit files here or click to choose them
              </Text>
            </div>
          )
        ) : (
          <ScrollArea.Autosize mah={280}>
            <Stack spacing={4}>
              {exhibits.map((exhibit, position) => (
                <Group
                  key={exhibit.id}
                  noWrap
                  spacing="sm"
                  className="rounded bg-gray-100 px-3 py-2"
                >
                  <Text size="sm" color="dimmed" className="w-6 shrink-0">
                    {position + 1}.
                  </Text>
                  <Text
                    size="sm"
                    weight={600}
                    className="min-w-0 flex-1 truncate"
                  >
                    {exhibit.name}
                  </Text>
                  <Text size="sm" color="dimmed" className="w-20 shrink-0">
                    {DESIGNATION_LABELS[exhibit.designation]}
                  </Text>
                  <div className="w-40 min-w-0 shrink-0">
                    <ExhibitFileLink
                      exhibit={exhibit}
                      onPreview={() =>
                        setPreviewing(exhibitPreviewFile(exhibit))
                      }
                    />
                  </div>
                  <div className="w-4 shrink-0">
                    {!exhibit.editable && <ReadOnlyExhibitIcon />}
                  </div>
                </Group>
              ))}
            </Stack>
          </ScrollArea.Autosize>
        )}

        <Group position="apart">
          {isOpen && (
            <Group spacing="xs">
              {addButton}
              {exhibits.length > 0 && (
                <Button
                  size="xs"
                  variant="default"
                  onClick={() => setExpanded(true)}
                >
                  Manage exhibits
                </Button>
              )}
              {submitButton}
            </Group>
          )}
          {exhibits.length > 0 && (
            <Tooltip label="Show all exhibits" withinPortal>
              <ActionIcon
                aria-label="Expand exhibits"
                variant="default"
                onClick={() => setExpanded(true)}
              >
                <EnterFullScreenIcon />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </div>

      {/* Outside the drop zone: React bubbles a drop from a portalled modal up to it. */}
      <ExhibitFilePreviewModal
        file={previewing}
        onClose={() => setPreviewing(null)}
      />
      <AddExhibitsModal
        event={event}
        opened={adding}
        existingCount={submission.exhibits.length}
        initialFiles={droppedFiles}
        onClose={() => {
          setAdding(false)
          setDroppedFiles([])
        }}
      />
      <ExhibitsTableModal
        event={event}
        submission={{ ...submission, exhibits }}
        opened={expanded && !adding}
        onClose={() => setExpanded(false)}
        actions={
          isOpen && (
            <>
              {addButton}
              {submitButton}
            </>
          )
        }
      />
    </>
  )
}

const NO_EXHIBITS_MESSAGE =
  'No exhibits were submitted to this event within the 15 business day window.'

const NoExhibitsDialog: React.FC<{ storageKey: string }> = ({ storageKey }) => {
  const [seen, setSeen] = useLocalStorage({
    key: storageKey,
    defaultValue: false,
    getInitialValueInEffect: false,
  })

  return (
    <Modal
      opened={!seen}
      onClose={() => setSeen(true)}
      title="No Exhibits"
      centered
    >
      <Stack>
        <Text size="sm">
          {NO_EXHIBITS_MESSAGE} If you need to make changes or updates please
          reach out to the email below:
        </Text>
        <Anchor size="sm" href={`mailto:${COURT_REPORTER_EXHIBITS_EMAIL}`}>
          {COURT_REPORTER_EXHIBITS_EMAIL}
        </Anchor>
        <Group position="right">
          <Button onClick={() => setSeen(true)}>Close</Button>
        </Group>
      </Stack>
    </Modal>
  )
}

const ContactUs: React.FC = () => (
  <>
    If you need to make changes or updates please reach out to{' '}
    <Anchor href={`mailto:${COURT_REPORTER_EXHIBITS_EMAIL}`}>
      {COURT_REPORTER_EXHIBITS_EMAIL}
    </Anchor>
    .
  </>
)

const SubmissionNotice: React.FC<{
  submission: CourtReporterExhibitSubmission
  state: ExhibitSubmissionState
}> = ({ submission, state }) => {
  if (state === 'open') {
    return submission.deadline ? (
      <Text size="sm" color="dimmed">
        Submit your exhibits by {formatDate(submission.deadline)}.
      </Text>
    ) : null
  }

  if (state === 'missed') {
    return (
      <Stack spacing={2}>
        <Text size="sm" weight={600}>
          No Exhibits
        </Text>
        <Text size="sm">
          {NO_EXHIBITS_MESSAGE} <ContactUs />
        </Text>
      </Stack>
    )
  }

  return (
    <Text size="sm">
      {state === 'submitted' &&
        (hasCourtReporterExhibits(submission)
          ? `You submitted these exhibits on ${formatDate(
              submission.submitted_at as string,
            )}. `
          : `You confirmed on ${formatDate(
              submission.submitted_at as string,
            )} that this event has no exhibits. `)}
      {state === 'closed' && 'Exhibit submission for this event is closed. '}
      <ContactUs />
    </Text>
  )
}

const SubmitExhibitsButton: React.FC<{
  event: ExhibitSubmissionEvent
  submission: CourtReporterExhibitSubmission
  allowNoExhibits: boolean
}> = ({ event, submission, allowNoExhibits }) => {
  const { openConfirmModal, openModal, closeModal } = useModals()
  const { mutate: submit, isLoading } = useSubmitExhibitsMutation(event)
  const blocker = submitBlocker(submission)
  // With none of their own, the CR confirms the event has no exhibits instead.
  const noExhibits = !hasCourtReporterExhibits(submission)

  const thank = () => {
    const modalId = 'exhibits-submitted'
    openModal({
      modalId,
      title: noExhibits ? 'Thank you' : 'Exhibits submitted',
      centered: true,
      children: (
        <Stack>
          <Text size="sm">
            {noExhibits
              ? 'Thank you. '
              : 'Thank you for submitting the exhibits for this event. '}
            <ContactUs />
          </Text>
          <Group position="right">
            <Button onClick={() => closeModal(modalId)}>Close</Button>
          </Group>
        </Stack>
      ),
    })
  }

  if (noExhibits && !allowNoExhibits) {
    return null
  }

  return (
    <Tooltip label={blocker} disabled={blocker === null} withinPortal>
      <div>
        <Button
          size="xs"
          variant={noExhibits ? 'default' : 'filled'}
          disabled={blocker !== null}
          loading={isLoading}
          onClick={() =>
            openConfirmModal({
              title: noExhibits
                ? 'No exhibits for this event?'
                : 'Submit exhibits?',
              children: (
                <Text size="sm">
                  {noExhibits
                    ? "You confirm this event has no exhibits. You can't add any afterwards."
                    : "Once submitted, you can't add, change or reorder these exhibits."}
                </Text>
              ),
              labels: {
                confirm: noExhibits ? 'Confirm' : 'Submit',
                cancel: 'Cancel',
              },
              onConfirm: () =>
                submit(undefined, {
                  onSuccess: thank,
                  onError: error =>
                    showNotification({
                      color: 'red',
                      message: getApiErrorMessage(
                        error,
                        'Could not submit the exhibits',
                      ),
                    }),
                }),
            })
          }
        >
          {noExhibits ? 'No exhibits for this event' : 'Submit exhibits'}
        </Button>
      </div>
    </Tooltip>
  )
}

const formatDate = (value: string) => format(parseISO(value), 'MM/dd/yyyy')
